// AddDatabaseModalCreateFlow.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import CreateTypeSection from 'components/addmodal/sections/createtype/CreateTypeSection';
import Tabs from 'components/tabs/Tabs';
import TypeFields from 'database/typefields/TypeFields';

// Hooks
import { CREATE_TAB_ABOUT, CREATE_TAB_FIELDS } from './useAddDatabaseModalState';

/**
 * AddDatabaseModalCreateFlow
 *
 * Renders the create-database tabs for metadata editing and field setup.
 *
 * @param {object} flowState - Modal flow state used to determine the active tab and copy.
 * @param {object} createTypeState - Create-type form state and handlers.
 * @param {string|null} workspaceKey - Active workspace key.
 *
 * @returns {ReactNode} The rendered create flow.
 */
const AddDatabaseModalCreateFlow = ({
    flowState,
    createTypeState,
    workspaceKey,
}) => {
    const { theme } = useTheme();

    return (
        <>
            <Tabs
                tabs={flowState.createTabs}
                selectedKey={flowState.selectedCreateTab}
                tabWidth={110}
            />

            {flowState.selectedCreateTab === CREATE_TAB_ABOUT && (
                <div className="add-database-modal-panel">
                    <CreateTypeSection
                        iconKey={createTypeState.iconKey}
                        setIconKey={createTypeState.setIconKey}
                        isSavingType={createTypeState.isSavingType || createTypeState.isUpdatingType}
                        canSaveType={flowState.hasCreatedType ? createTypeState.canSaveCreatedType : createTypeState.canSaveType}
                        label={createTypeState.label}
                        setLabel={createTypeState.setLabel}
                        labelHelperText={createTypeState.labelHelperText}
                        onCreateType={flowState.hasCreatedType ? createTypeState.handleSaveCreatedType : createTypeState.handleCreateType}
                        labelPlural={createTypeState.labelPlural}
                        onLabelPluralChange={createTypeState.handleLabelPluralChange}
                        pluralHelperText={createTypeState.pluralHelperText}
                        description={createTypeState.description}
                        setDescription={createTypeState.setDescription}
                        defaultView={createTypeState.defaultView}
                        setDefaultView={createTypeState.setDefaultView}
                        showSubmitButton={false}
                    />
                </div>
            )}

            {flowState.selectedCreateTab === CREATE_TAB_FIELDS && (
                <div className="add-database-modal-fields">
                    <div
                        className="add-database-modal-fields-hint"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        {`Your database is ready. What fields would be useful for each ${flowState.createdTypeLabelLower} in this database?`}
                    </div>

                    <div className="add-database-modal-fields-content">
                        <TypeFields
                            typeKey={createTypeState.newTypeKey}
                            workspaceKey={workspaceKey}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default AddDatabaseModalCreateFlow;